import React, { useEffect, useState } from 'react';
import { AlertBanner, Button, ButtonBar, ConfirmButton } from '@ygtechnologies/rubus-ui';
import { FollowerRepairProgress, getFollowerRepairProgress, recalculateFollowersForAllProjects } from '@/services/projects';

// The progress query aggregates over tens of millions of follow rows and has been measured at
// ~13 seconds against production-sized data. Polling every 10 seconds started a new scan before the
// previous one finished and piled load onto the table the sweep is writing to. One minute, and the
// Refresh button for when you want it sooner.
const PollIntervalMs = 60000;

/**
 * RUC-215 repair sweep. Estate-wide, so it lives behind the Admin-gated settings area rather
 * than on a project page.
 */
const FollowerMaintenance: React.FC = () => {
    const [fromProjectId, setFromProjectId] = useState('');
    const [isBusy, setIsBusy] = useState(false);
    const [queued, setQueued] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<FollowerRepairProgress | null>(null);
    const [checkedAt, setCheckedAt] = useState<string | null>(null);

    const refresh = async () => {
        try {
            setProgress(await getFollowerRepairProgress());
            setCheckedAt(new Date().toLocaleTimeString());
        } catch {
            // A failed poll is not worth interrupting the page for; the timestamp stops advancing.
        }
    };

    useEffect(() => {
        refresh();
        const timer = setInterval(refresh, PollIntervalMs);
        return () => clearInterval(timer);
    }, []);

    const start = async () => {
        setIsBusy(true);
        setError(null);
        try {
            await recalculateFollowersForAllProjects(fromProjectId ? Number(fromProjectId) : 0);
            setQueued(true);
        } catch {
            setError('The request failed. Check that the AM backend is reachable.');
        } finally {
            setIsBusy(false);
        }
    };

    return (
        <div>
            <h3>Recalculate chat followers for all projects</h3>
            <p>
                Rebuilds who follows each case and unit chat across every AM project, one project at a time in the
                background. Needed where follows were wrongly removed: those members get no yellow dots and no unread
                reminder emails until their follows are recalculated. Revived follows start counting from now, so this does
                not resurface old messages as unread.
            </p>
            <p>
                Progress is written to the AM backend log, one line per project. Do not deploy AM while this is running:
                queued work is held in memory and a restart loses it. If that happens, start again from the last project id
                in the log. Re-running a project is harmless.
            </p>
            <p>
                <label>
                    Resume from project id (optional){' '}
                    <input
                        type="number"
                        className="form-control"
                        value={fromProjectId}
                        onChange={(e) => setFromProjectId(e.target.value)}
                        placeholder="0"
                        style={{ width: '8rem' }}
                    />
                </label>
            </p>
            <ButtonBar margins="bottom">
                <ConfirmButton
                    variant="primary"
                    size="md"
                    disabled={isBusy}
                    dialogTitle="Start the follower sweep?"
                    dialogBody="Every AM project will have its chat followers rebuilt, one at a time in the background. Make sure the RUC-215 backend fixes are deployed first, or follows will be restored with their original dates."
                    onConfirm={start}>
                    Start sweep
                </ConfirmButton>
            </ButtonBar>

            {queued && (
                <AlertBanner type="success">
                    Sweep queued. Progress below refreshes every {PollIntervalMs / 1000} seconds.
                </AlertBanner>
            )}
            {error && <AlertBanner type="danger">{error}</AlertBanner>}

            <h4>Wiped follows awaiting revival</h4>
            <p>
                Counted from the follow table itself, not from the job, so it stays accurate even if the sweep dies part way.
                It counts soft-deleted follows of the three AM follow types belonging to members the recalculation would
                actually consider, which is the RUC-215 damage signature, and it should fall towards zero as the sweep runs.
            </p>
            <p>
                <strong>Zero here does not mean there is nothing to do.</strong> This counts follows that were removed and
                need reviving. It cannot count follows that were never created, because working out which follows{' '}
                <em>should</em> exist means running the participant, permission and opt-out logic rather than a query. Use
                Dry run on a project&apos;s Tools tab for that: a project reporting nothing here can still report a large
                &quot;would add&quot;. Legacy follows with no follow type are excluded as well, since the AM recalculation
                cannot repair them.
            </p>
            {progress === null ? (
                <p>Loading…</p>
            ) : (
                <>
                    <p>
                        <strong>{progress.repairableDeadFollows.toLocaleString()}</strong> wiped follows awaiting revival
                        across <strong>{progress.projectsRemaining}</strong> projects.
                        {progress.lowestProjectIdRemaining !== null && (
                            <>
                                {' '}
                                Lowest project id remaining is <strong>{progress.lowestProjectIdRemaining}</strong>; since
                                the sweep works upwards through project ids, that is roughly where it has reached.
                            </>
                        )}
                        {checkedAt && <> Last checked {checkedAt}.</>}
                    </p>
                    <ButtonBar margins="bottom">
                        <Button variant="secondary" size="md" onClick={refresh}>
                            Refresh now
                        </Button>
                    </ButtonBar>
                    {progress.projects.length > 0 && (
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Project</th>
                                    <th>Name</th>
                                    <th style={{ textAlign: 'right' }}>Awaiting revival</th>
                                </tr>
                            </thead>
                            <tbody>
                                {progress.projects.map((p) => (
                                    <tr key={p.projectId}>
                                        <td>{p.projectId}</td>
                                        <td>{p.projectName}</td>
                                        <td style={{ textAlign: 'right' }}>{p.deadFollows.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}
        </div>
    );
};

export default FollowerMaintenance;
